/*
 * Author: Valeria Sinnati
 *
 * Admin user-detail page. Includes a Voice section that lets admin and
 * superadmin review the user's original recording and the cloned-voice
 * preview to assess quality (audio is streamed, with auth, from
 * /admin/users/{id}/voice/recording and /voice/clone-preview).
 *
 *   |\---/|
 *   | o_o |
 *    \_^_/
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getUserDetail,
  updateUser,
  disconnectWhatsApp,
  getUserActivity,
  updateUserFeatureFlags,
  getUserSocialScopes,
  getUserVoiceRecording,
  getUserVoiceClonePreview,
} from '../../services/adminApi';
import ScopesPanel from '../components/ScopesPanel';
import { notify, confirmAsync } from '../../utils/notify';
import './UserDetail.css';

function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const currentAdmin = useSelector((state) => state.auth.user);
  const isSuperadmin = currentAdmin?.role === 'superadmin';
  const [user, setUser] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  // Granted-scopes inspection (superadmin-only). Lazy-loaded on demand
  // because debug_token is a network round-trip per platform.
  const [scopesData, setScopesData] = useState(null);     // { instagram: {...}, facebook_page: {...}, tiktok: {...} }
  const [scopesLoading, setScopesLoading] = useState(false);
  const [scopesError, setScopesError] = useState(null);
  const [scopesVisible, setScopesVisible] = useState(false);
  // Voice audio review (lazy-loaded blobs). URLs are object URLs created
  // from the authenticated blob fetch; revoked on change/unmount below.
  const [voiceRecordingUrl, setVoiceRecordingUrl] = useState(null);
  const [voiceCloneUrl, setVoiceCloneUrl] = useState(null);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [loadingClone, setLoadingClone] = useState(false);
  const [recordingError, setRecordingError] = useState(null);
  const [cloneError, setCloneError] = useState(null);

  useEffect(() => {
    loadUser();
    loadActivity();
  }, [userId]);

  const loadUser = async () => {
    try {
      setLoading(true);
      const response = await getUserDetail(userId);
      setUser(response.data);
      setFormData({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        phone_number: response.data.phone_number || '',
        is_active: response.data.is_active
      });
    } catch (err) {
      console.error('Failed to load user:', err);
      notify.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    try {
      const response = await getUserActivity(userId);
      setActivity(response.data);
    } catch (err) {
      console.error('Failed to load activity:', err);
    }
  };

  // Toggle the granted-OAuth-scopes panel for the connected socials.
  // Calls /admin/users/{id}/social-scopes only the first time it's
  // opened; subsequent toggles just flip the visibility flag. Reload
  // is exposed via the refresh button below.
  const handleToggleScopes = async () => {
    if (scopesVisible) {
      setScopesVisible(false);
      return;
    }
    setScopesVisible(true);
    if (scopesData) return; // already loaded — just reveal
    await loadScopes();
  };

  const loadScopes = async () => {
    try {
      setScopesLoading(true);
      setScopesError(null);
      const res = await getUserSocialScopes(userId);
      setScopesData(res.data?.platforms || {});
    } catch (err) {
      console.error('Failed to load social scopes:', err);
      setScopesError(err.response?.data?.detail || 'Failed to load granted permissions');
    } finally {
      setScopesLoading(false);
    }
  };

  // Revoke object URLs when they change or on unmount to avoid leaks.
  useEffect(() => {
    return () => { if (voiceRecordingUrl) URL.revokeObjectURL(voiceRecordingUrl); };
  }, [voiceRecordingUrl]);
  useEffect(() => {
    return () => { if (voiceCloneUrl) URL.revokeObjectURL(voiceCloneUrl); };
  }, [voiceCloneUrl]);

  const loadVoiceRecording = async () => {
    setRecordingError(null);
    setLoadingRecording(true);
    try {
      const res = await getUserVoiceRecording(userId);
      setVoiceRecordingUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch (err) {
      setRecordingError(err.response?.status === 404 ? 'No recording available' : 'Failed to load recording');
    } finally {
      setLoadingRecording(false);
    }
  };

  const loadVoiceClone = async () => {
    setCloneError(null);
    setLoadingClone(true);
    try {
      const res = await getUserVoiceClonePreview(userId);
      setVoiceCloneUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch (err) {
      setCloneError(err.response?.status === 404 ? 'No cloned-voice preview available' : 'Failed to load cloned voice');
    } finally {
      setLoadingClone(false);
    }
  };

  const handleDisconnectWhatsApp = async (whatsappId) => {
    if (!(await confirmAsync({ message: 'Are you sure you want to disconnect this WhatsApp number?', danger: true }))) {
      return;
    }
    try {
      await disconnectWhatsApp(userId, whatsappId);
      loadUser();
      loadActivity();
      notify.success('WhatsApp number disconnected successfully');
    } catch (err) {
      notify.error('Failed to disconnect WhatsApp number');
    }
  };

  const handleSave = async () => {
    try {
      await updateUser(userId, formData);
      setEditing(false);
      loadUser();
    } catch (err) {
      notify.error('Failed to update user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="user-detail-loading">Loading user details...</div>;
  }

  if (!user) {
    return <div className="user-detail-error">User not found</div>;
  }

  return (
    <div className="user-detail">
      <div className="user-detail-header">
        <button className="back-button" onClick={() => navigate('/admin/users')}>
          ← Back to Users
        </button>
        <h2>User Details</h2>
        {!editing && (
          <button className="edit-button" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}
      </div>

      <div className="user-detail-content">
        <div className="detail-section">
          <h3>Profile Information</h3>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Email</label>
              <div className="detail-value">{user.email}</div>
            </div>
            <div className="detail-item">
              <label>First Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                />
              ) : (
                <div className="detail-value">{user.first_name || '-'}</div>
              )}
            </div>
            <div className="detail-item">
              <label>Last Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              ) : (
                <div className="detail-value">{user.last_name || '-'}</div>
              )}
            </div>
            <div className="detail-item">
              <label>Phone Number</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                />
              ) : (
                <div className="detail-value">{user.phone_number || '-'}</div>
              )}
            </div>
            <div className="detail-item">
              <label>Preferred Language</label>
              <div className="detail-value">{user.preferred_language || 'en'}</div>
            </div>
            <div className="detail-item">
              <label>Account Status</label>
              {editing ? (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              ) : (
                <div className="detail-value">
                  <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              )}
            </div>
            <div className="detail-item">
              <label>Subscription Status</label>
              <div className="detail-value">
                <span className={`status-badge ${user.subscription_status || 'active'}`}>
                  {(user.subscription_status || 'active').charAt(0).toUpperCase() + (user.subscription_status || 'active').slice(1)}
                </span>
                {/* For trial users, also show active badge to indicate they're active */}
                {user.subscription_status === 'trial' && user.is_active && (
                  <span className="status-badge active" style={{ marginLeft: '5px' }}>
                    Active
                  </span>
                )}
              </div>
            </div>
            <div className="detail-item">
              <label>Registration Date</label>
              <div className="detail-value">{formatDate(user.created_at)}</div>
            </div>
            <div className="detail-item">
              <label>Last Login</label>
              <div className="detail-value">{formatDate(user.last_login_at)}</div>
            </div>
            <div className="detail-item">
              <label>Terms Accepted</label>
              <div className="detail-value">{formatDate(user.terms_accepted_at)}</div>
            </div>
            <div className="detail-item">
              <label>Privacy Accepted</label>
              <div className="detail-value">{formatDate(user.privacy_accepted_at)}</div>
            </div>
            {user.affiliate_code && (
              <div className="detail-item">
                <label>Affiliate Code</label>
                <div className="detail-value">{user.affiliate_code}</div>
              </div>
            )}
          </div>
          {editing && (
            <div className="edit-actions">
              <button className="save-button" onClick={handleSave}>
                Save
              </button>
              <button className="cancel-button" onClick={() => {
                setEditing(false);
                loadUser();
              }}>
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="detail-section">
          <h3>WhatsApp Numbers</h3>
          {user.whatsapp_numbers && user.whatsapp_numbers.length > 0 ? (
            <div className="whatsapp-list">
              {user.whatsapp_numbers.map((wa) => (
                <div key={wa.id} className="whatsapp-item">
                  <div className="wa-info">
                    <div className="wa-phone">{wa.phone_number}</div>
                    <div className="wa-meta">
                      <span className="wa-type">{wa.agent_type}</span>
                      <span className={`wa-status ${wa.is_connected ? 'connected' : 'disconnected'}`}>
                        {wa.is_connected ? 'Connected' : 'Disconnected'}
                      </span>
                      {wa.webhook_verified && (
                        <span className="wa-verified">✓ Verified</span>
                      )}
                    </div>
                  </div>
                  {wa.is_connected && (
                    <button
                      className="btn-disconnect"
                      onClick={() => handleDisconnectWhatsApp(wa.id)}
                    >
                      Disconnect
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data">No WhatsApp numbers connected</div>
          )}
        </div>

        {/* ── Voice (recording + cloned voice) ── */}
        <div className="detail-section">
          <h3>Voice</h3>
          <div className="voice-review-grid">
            {/* User recording */}
            <div className="voice-review-card">
              <div className="voice-review-title">User recording</div>
              {user.voice?.has_recording ? (
                <>
                  <div className="voice-review-meta">
                    <span>Duration: {Math.round(user.voice.recording_duration || 0)}s</span>
                    <span className={user.voice.meets_duration ? 'success' : 'failure'}>
                      {user.voice.meets_duration ? '✓ ≥ 30s' : '⚠ too short'}
                    </span>
                    {user.voice.recorded_at && <span>Recorded: {formatDate(user.voice.recorded_at)}</span>}
                  </div>
                  {voiceRecordingUrl ? (
                    <audio controls src={voiceRecordingUrl} style={{ width: '100%', marginTop: 8 }} />
                  ) : (
                    <button
                      type="button"
                      className="btn-show-scopes"
                      onClick={loadVoiceRecording}
                      disabled={loadingRecording}
                      style={{ marginTop: 8 }}
                    >
                      {loadingRecording ? '⏳ Loading…' : '▶ Load recording'}
                    </button>
                  )}
                  {recordingError && <div className="social-scopes-error">⚠️ {recordingError}</div>}
                </>
              ) : (
                <div className="no-data">No recording on file</div>
              )}
            </div>

            {/* Cloned voice */}
            <div className="voice-review-card">
              <div className="voice-review-title">Cloned voice</div>
              {user.voice?.has_clone ? (
                <>
                  <div className="voice-review-meta">
                    {user.voice.voice_name && <span>Name: {user.voice.voice_name}</span>}
                    <span className="status-badge active">{user.voice.clone_status}</span>
                    {user.voice.cloned_at && <span>Cloned: {formatDate(user.voice.cloned_at)}</span>}
                  </div>
                  {voiceCloneUrl ? (
                    <audio controls src={voiceCloneUrl} style={{ width: '100%', marginTop: 8 }} />
                  ) : (
                    <button
                      type="button"
                      className="btn-show-scopes"
                      onClick={loadVoiceClone}
                      disabled={loadingClone}
                      style={{ marginTop: 8 }}
                    >
                      {loadingClone ? '⏳ Loading…' : '▶ Load cloned voice'}
                    </button>
                  )}
                  {cloneError && <div className="social-scopes-error">⚠️ {cloneError}</div>}
                </>
              ) : user.voice?.clone_status ? (
                <div className="no-data">
                  Clone status: {user.voice.clone_status} — preview not available
                </div>
              ) : (
                <div className="no-data">No cloned voice</div>
              )}
            </div>
          </div>
        </div>

        {/* ── Social Connections ── */}
        <div className="detail-section">
          <div className="detail-section-header">
            <h3>Social Connections</h3>
            {isSuperadmin && (
              <div className="social-scopes-controls">
                <button
                  type="button"
                  className="btn-show-scopes"
                  onClick={handleToggleScopes}
                  disabled={scopesLoading}
                  title="Inspect granted OAuth scopes (superadmin)"
                >
                  {scopesLoading
                    ? '⏳ Loading…'
                    : scopesVisible
                      ? '🔒 Hide permissions'
                      : '🔍 Show granted permissions'}
                </button>
                {scopesVisible && scopesData && (
                  <button
                    type="button"
                    className="btn-show-scopes-refresh"
                    onClick={loadScopes}
                    disabled={scopesLoading}
                    title="Re-query Meta debug_token"
                  >
                    ↻ Refresh
                  </button>
                )}
              </div>
            )}
          </div>
          {scopesError && (
            <div className="social-scopes-error">⚠️ {scopesError}</div>
          )}
          <div className="social-connections-grid">
            {(user.social_connections || []).map((conn) => {
              const platformConfig = {
                instagram: { icon: '📸', label: 'Instagram', color: '#E4405F' },
                facebook_page: { icon: '📘', label: 'Facebook Page', color: '#1877F2' },
                tiktok: { icon: '🎵', label: 'TikTok', color: '#000000' },
              };
              const cfg = platformConfig[conn.platform] || { icon: '🔗', label: conn.platform, color: '#666' };
              return (
                <div key={conn.platform} className={`social-conn-card ${conn.connected ? 'connected' : 'not-connected'}`}>
                  <div className="social-conn-header" style={{ borderTopColor: cfg.color }}>
                    <span className="social-conn-icon">{cfg.icon}</span>
                    <span className="social-conn-platform">{cfg.label}</span>
                    <span className={`social-conn-badge ${conn.connected ? 'badge-connected' : 'badge-not-connected'}`}>
                      {conn.connected ? 'Connected' : 'Not Connected'}
                    </span>
                  </div>
                  {conn.connected ? (
                    <div className="social-conn-body">
                      {conn.username && (
                        <div className="social-conn-row">
                          <span className="social-conn-label">Username</span>
                          <span className="social-conn-value">@{conn.username}</span>
                        </div>
                      )}
                      {conn.name && (
                        <div className="social-conn-row">
                          <span className="social-conn-label">Name</span>
                          <span className="social-conn-value">{conn.name}</span>
                        </div>
                      )}
                      {conn.page_name && conn.platform === 'facebook_page' && (
                        <div className="social-conn-row">
                          <span className="social-conn-label">Page</span>
                          <span className="social-conn-value">{conn.page_name}</span>
                        </div>
                      )}
                      {conn.followers_count != null && (
                        <div className="social-conn-row">
                          <span className="social-conn-label">Followers</span>
                          <span className="social-conn-value">{conn.followers_count.toLocaleString()}</span>
                        </div>
                      )}
                      {conn.media_count != null && (
                        <div className="social-conn-row">
                          <span className="social-conn-label">Media</span>
                          <span className="social-conn-value">{conn.media_count.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="social-conn-row">
                        <span className="social-conn-label">Token</span>
                        <span className={`social-conn-token ${conn.is_token_valid ? 'token-valid' : 'token-expired'}`}>
                          {conn.is_token_valid ? '✓ Valid' : '✗ Expired'}
                        </span>
                      </div>
                      {conn.token_expires_at && (
                        <div className="social-conn-row">
                          <span className="social-conn-label">Expires</span>
                          <span className="social-conn-value">{formatDate(conn.token_expires_at)}</span>
                        </div>
                      )}
                      {conn.connected_at && (
                        <div className="social-conn-row">
                          <span className="social-conn-label">Connected</span>
                          <span className="social-conn-value">{formatDate(conn.connected_at)}</span>
                        </div>
                      )}
                      {conn.last_post_at && (
                        <div className="social-conn-row">
                          <span className="social-conn-label">Last Post</span>
                          <span className="social-conn-value">{formatDate(conn.last_post_at)}</span>
                        </div>
                      )}
                      {/* Granted-scopes panel — superadmin only, lazy-loaded */}
                      {isSuperadmin && scopesVisible && scopesData && scopesData[conn.platform] && (
                        <ScopesPanel platform={conn.platform} info={scopesData[conn.platform]} />
                      )}
                    </div>
                  ) : (
                    <div className="social-conn-empty">
                      Not connected yet
                    </div>
                  )}
                </div>
              );
            })}
            {(!user.social_connections || user.social_connections.length === 0) && (
              <div className="no-data">No social platforms available</div>
            )}
          </div>
        </div>

        {/* ── Feature Access ── */}
        <div className="detail-section">
          <h3>Feature Access</h3>
          <p className="feature-flags-description">
            Control which features this user can access. Changes are saved immediately.
          </p>
          <div className="feature-flags-grid">
            {[
              { key: 'can_connect_socials', label: 'Connect Social Accounts', icon: '🔗', description: 'Master switch — must be ON for per-platform flags to work', isMaster: true },
              { key: 'can_connect_instagram', label: 'Instagram', icon: '📸', description: 'Allow connecting Instagram account', parent: 'can_connect_socials' },
              { key: 'can_connect_facebook', label: 'Facebook', icon: '📘', description: 'Allow connecting Facebook Page', parent: 'can_connect_socials' },
              { key: 'can_connect_tiktok', label: 'TikTok', icon: '🎵', description: 'Allow connecting TikTok account', parent: 'can_connect_socials' },
              { key: 'can_generate_posts', label: 'Generate Posts', icon: '✨', description: 'Allow generating weekly content and videos' },
            ].map(({ key, label, icon, description, isMaster, parent }) => {
              const flags = user.feature_flags || {};
              const isEnabled = !!flags[key];
              const isParentOff = parent && !flags[parent];
              return (
                <div key={key} className={`feature-flag-card ${isEnabled && !isParentOff ? 'enabled' : 'disabled'}${parent ? ' feature-flag-child' : ''}${isMaster ? ' feature-flag-master' : ''}`}>
                  <div className="feature-flag-header">
                    <span className="feature-flag-icon">{icon}</span>
                    <span className="feature-flag-label">{label}</span>
                    {isParentOff && <span className="feature-flag-locked" title="Master switch is OFF">🔒</span>}
                    <label className="feature-flag-toggle">
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        disabled={isParentOff}
                        onChange={async (e) => {
                          const newFlags = { ...flags, [key]: e.target.checked };
                          // When turning OFF master, also turn off children visually
                          if (isMaster && !e.target.checked) {
                            newFlags.can_connect_instagram = false;
                            newFlags.can_connect_facebook = false;
                            newFlags.can_connect_tiktok = false;
                          }
                          // When turning ON master, also turn on all children
                          if (isMaster && e.target.checked) {
                            newFlags.can_connect_instagram = true;
                            newFlags.can_connect_facebook = true;
                            newFlags.can_connect_tiktok = true;
                          }
                          try {
                            await updateUserFeatureFlags(userId, newFlags);
                            setUser({ ...user, feature_flags: newFlags });
                          } catch (err) {
                            notify.error('Failed to update feature flag');
                          }
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <p className="feature-flag-desc">{description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {activity && (
          <div className="detail-section">
            <h3>Activity Log</h3>
            
            {activity.login_history && activity.login_history.length > 0 && (
              <div className="activity-subsection">
                <h4>Login History</h4>
                <div className="activity-list">
                  {activity.login_history.slice(0, 10).map((log, idx) => (
                    <div key={idx} className="activity-item">
                      <div className="activity-action">{log.action}</div>
                      <div className="activity-details">
                        <span>IP: {log.ip_address || 'N/A'}</span>
                        <span>{formatDate(log.created_at)}</span>
                        {log.success !== undefined && (
                          <span className={log.success ? 'success' : 'failure'}>
                            {log.success ? '✓' : '✗'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activity.active_sessions && activity.active_sessions.length > 0 && (
              <div className="activity-subsection">
                <h4>Active Sessions ({activity.active_sessions.length})</h4>
                <div className="activity-list">
                  {activity.active_sessions.map((session) => (
                    <div key={session.id} className="activity-item">
                      <div className="activity-action">Active Session</div>
                      <div className="activity-details">
                        <span>{session.device_info || 'Unknown device'}</span>
                        <span>IP: {session.ip_address || 'N/A'}</span>
                        <span>Last activity: {formatDate(session.last_activity)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activity.whatsapp_events && activity.whatsapp_events.length > 0 && (
              <div className="activity-subsection">
                <h4>WhatsApp Events</h4>
                <div className="activity-list">
                  {activity.whatsapp_events.map((event, idx) => (
                    <div key={idx} className="activity-item">
                      <div className="activity-action">{event.action}</div>
                      <div className="activity-details">
                        <span>By: {event.admin_email || 'System'}</span>
                        <span>{formatDate(event.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!activity.login_history || activity.login_history.length === 0) &&
             (!activity.whatsapp_events || activity.whatsapp_events.length === 0) && (
              <div className="no-data">No activity recorded</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDetail;

